import { toast } from 'react-toastify'
import {
  cleanObject,
  getErrorMessage,
  getFilenameFromDisposition,
} from '../../helpers'
import type { BaseApiResponse, QueryProps } from '../shared/shared.type'
import type { ReportType } from '../reports/reports.type'
import { apiSlice } from '../api/apiSlice'
import { ErrorType } from '../shared/shared.type'
import {
  GetAllRefundsQuery,
  GetAllRefundsResponse,
  GetOrderRefund,
  InitiateRefund,
} from '../refunds/refunds.type'
import {
  SingleTransactionResponse,
  TransactionsResponse,
} from './transactions.type'

export const transactionsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllTransactions: builder.query<
      TransactionsResponse,
      QueryProps & { status?: string; fulfilmentCentre?: string }
    >({
      query: (params) => {
        const updatedParams = cleanObject(params)

        return {
          url: `v1/transactions`,
          params: {
            ...updatedParams,
          },
          method: 'GET',
        }
      },
      providesTags: [{ type: 'TRANSACTION', id: 'LIST' }],
    }),
    getSingleTransaction: builder.query<
      SingleTransactionResponse,
      { id?: string }
    >({
      query: (args) => ({
        url: `v1/transactions/${args.id}/details`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'TRANSACTION', id: arg.id },
      ],
    }),
    exportReports: builder.mutation<BaseApiResponse, any>({
      query: (args) => ({
        url: `v1/reports/export`,
        params: args,
        method: 'GET',
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    downloadReports: builder.mutation<BaseApiResponse, any>({
      query: ({ ...payload }) => {
        return {
          url: 'v1/reports/export-download',
          method: 'GET',
          params: { ...payload },
          responseHandler: async (response) => {
            if (response.ok) {
              const reportType = payload.reportType as ReportType

              const filenames: Record<ReportType, string> = {
                general_settlements: 'Settlements Report.xlsx',
                transactions: 'Transactions Report.xlsx',
                vendor_settlements: 'Creator Settlements Report.xlsx',
                creators: 'Creators Report.xlsx',
                customers: 'Customers Report.xlsx',
                items_summary: 'Items Summary Report.xlsx',
                sales_rep_analytics: 'Sales Rep Analytics Report.xlsx',
                event_summary: 'Event Summary Report.xlsx',
                event_tickets: 'Event Tickets Report.xlsx',
                top_products_by_category:
                  'Top Products by Category Report.xlsx',
                daily_order_summary: 'Orders Report.xlsx',
                platform_cost_metrics: 'Platform Cost Metrics.xlsx',
              }

              const filename_ = getFilenameFromDisposition(
                response,
                filenames[reportType] ?? 'Export.xlsx',
              )

              const blob = await response.blob()

              const url = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = filename_
              document.body.appendChild(a)
              a.click()
              a.remove()
              window.URL.revokeObjectURL(url)

              return undefined
            }

            return response.json()
          },
          responseType: 'blob',
        }
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    initiateRefund: builder.mutation<BaseApiResponse, InitiateRefund>({
      query: (payload) => ({
        url: `v1/refunds`,
        method: 'POST',
        body: { ...payload },
      }),
      invalidatesTags: [
        { type: 'REFUND', id: 'LIST' },
        'REFUND',
        { type: 'TRANSACTION', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getAllRefunds: builder.query<GetAllRefundsResponse, GetAllRefundsQuery>({
      query: (args) => ({
        url: `v1/refunds`,
        params: {
          ...args,
        },
        method: 'GET',
      }),
      providesTags: [{ type: 'REFUND', id: 'LIST' }],
    }),
    getOrderRefund: builder.query<GetOrderRefund, { id: string }>({
      query: (args) => ({
        url: `v1/refunds/order/${args.id}`,
        params: {
          ...args,
        },
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'REFUND', id: `ORDER-${arg.id}` },
        'REFUND',
      ],
    }),
    approveRefund: builder.mutation<
      BaseApiResponse,
      { id: string; password: string }
    >({
      query: ({ id, ...payload }) => ({
        url: `v1/refunds/${id}/approve`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'REFUND', id: arg.id },
        { type: 'REFUND', id: 'LIST' },
        'REFUND',
        { type: 'TRANSACTION', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    cancelRefund: builder.mutation<BaseApiResponse, { id: string }>({
      query: (payload) => ({
        url: `v1/refunds/${payload.id}/cancel`,
        method: 'PATCH',
        body: undefined,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'REFUND', id: arg.id },
        { type: 'REFUND', id: 'LIST' },
        'REFUND',
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    declineRefund: builder.mutation<
      BaseApiResponse,
      { id: string; reason: string }
    >({
      query: ({ id, ...payload }) => ({
        url: `v1/refunds/${id}/reject`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'REFUND', id: arg.id },
        { type: 'REFUND', id: 'LIST' },
        'REFUND',
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
  }),
})

export const {
  useGetAllTransactionsQuery,
  useExportReportsMutation,
  useGetSingleTransactionQuery,
  useInitiateRefundMutation,
  useGetAllRefundsQuery,
  useGetOrderRefundQuery,
  useCancelRefundMutation,
  useApproveRefundMutation,
  useDeclineRefundMutation,
  useDownloadReportsMutation,
} = transactionsApiSlice
